import { mPerLon } from '../lib/geo';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import { Check, X, RotateCcw, RotateCw, Move, FlipHorizontal2, FlipVertical2, Copy } from 'lucide-react';
import { rectFromParams, rotatePts, pitCenter, moveBy } from '../lib/excavation';
import { useAe, setAe, closeArea, editArea, centroid, bearing, norm, turn, shift, rotHandle, mirror, besideRight } from '../lib/areaEdit';

const hIcon = (cls, testid, html = '') => L.divIcon({ className: 'ae-wrap', html: `<span class="ae-h ${cls}" data-testid="${testid}">${html}</span>`, iconSize: [26, 26], iconAnchor: [13, 13] });

// map handles: centre = move, top = rotate, vertices = reshape
export function useAreaEditLayer(mapRef) {
  const ae = useAe();
  const gRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    if (!ae) return undefined;
    const g = L.layerGroup().addTo(map);
    gRef.current = g;
    let pts = ae.pts, rot = ae.rot;
    const poly = (pts.length > 2 ? L.polygon : L.polyline)(pts, { color: '#f59e0b', weight: 3, dashArray: '7 5', fillColor: '#fde68a', fillOpacity: 0.3, interactive: false, className: 'ae-poly' }).addTo(g);
    const rotLine = L.polyline([centroid(pts), rotHandle(pts, rot)], { color: '#f59e0b', weight: 1.5, dashArray: '3 4', interactive: false }).addTo(g);
    const mk = (ll, cls, testid, html) => L.marker(ll, { draggable: true, zIndexOffset: 1500, icon: hIcon(cls, testid, html) }).addTo(g);
    const verts = pts.map((p, i) => mk(p, 'ae-v', `area-handle-v-${i}`));
    const mv = mk(centroid(pts), 'ae-move', 'area-handle-move', '✥');
    const rh = mk(rotHandle(pts, rot), 'ae-rot', 'area-handle-rot', '⟳');
    const sync = (skip) => {
      poly.setLatLngs(pts);
      const c = centroid(pts);
      verts.forEach((m, i) => m !== skip && m.setLatLng(pts[i]));
      if (mv !== skip) mv.setLatLng(c);
      if (rh !== skip) rh.setLatLng(rotHandle(pts, rot));
      rotLine.setLatLngs([c, rh === skip ? rh.getLatLng() : rotHandle(pts, rot)]);
    };
    const ll = (m) => { const p = m.getLatLng(); return [p.lat, p.lng]; };
    const commit = () => setAe({ pts, rot });
    verts.forEach((m, i) => { m.on('drag', () => { pts = pts.map((q, k) => (k === i ? ll(m) : q)); sync(m); }); m.on('dragend', commit); });
    let base = null;
    mv.on('dragstart', () => { base = { pts, c: centroid(pts) }; });
    mv.on('drag', () => { pts = shift(base.pts, base.c, ll(mv)); sync(mv); });
    mv.on('dragend', commit);
    rh.on('dragstart', () => { base = { pts, c: centroid(pts), rot, b: bearing(centroid(pts), ll(rh)) }; });
    rh.on('drag', () => { const d = bearing(base.c, ll(rh)) - base.b; pts = turn(base.pts, d, base.c); rot = base.rot + d; sync(rh); });
    rh.on('dragend', commit);
    return () => g.remove();
  }, [mapRef, ae]);
}

// bottom bar: angle, ±15°, done / cancel
export function AreaEditBar({ t }) {
  const ae = useAe();
  if (!ae) return null;
  const step = (d) => setAe({ pts: turn(ae.pts, d), rot: ae.rot + d });
  return createPortal(
    <div className="ae-bar" data-testid="area-edit-bar">
      <div className="ae-title"><Move size={15} /> <b>{ae.label || t.aeTitle}</b><small>{t.aeHint}</small></div>
      <div className="ae-row">
        <button className="btn btn-outline btn-sm" data-testid="area-rot-minus" onClick={() => step(-15)}><RotateCcw size={14} /> 15°</button>
        <span className="ae-deg" data-testid="area-rot-display">{norm(ae.rot)}°</span>
        <button className="btn btn-outline btn-sm" data-testid="area-rot-plus" onClick={() => step(15)}><RotateCw size={14} /> 15°</button>
      </div>
      <div className="ae-row">
        <button className="btn btn-outline btn-sm" data-testid="area-mirror-lr" onClick={() => setAe({ pts: mirror(ae.pts, 'lr') })}><FlipHorizontal2 size={14} /> {t.aeMirrorLr}</button>
        <button className="btn btn-outline btn-sm" data-testid="area-mirror-ud" onClick={() => setAe({ pts: mirror(ae.pts, 'ud') })}><FlipVertical2 size={14} /> {t.aeMirrorUd}</button>
        {ae.duplicate && <button className="btn btn-outline btn-sm" data-testid="area-duplicate" onClick={() => { const cp = besideRight(ae.pts); closeArea(true); editArea(ae.duplicate(cp)); }}><Copy size={14} /> {t.aeDup}</button>}
      </div>
      <div className="ae-row">
        {ae.extra && <button className="btn btn-dark btn-sm" data-testid="area-edit-extra" onClick={() => { closeArea(true); ae.extra.onClick(); }}>{ae.extra.label}</button>}
        <button className="btn btn-outline btn-sm" data-testid="area-edit-cancel" onClick={() => closeArea(false)}><X size={14} /> {t.btnCancel}</button>
        <button className="btn btn-green btn-sm" data-testid="area-edit-done" onClick={() => closeArea(true)}><Check size={14} /> {t.btnFinish}</button>
      </div>
    </div>,
    document.body,
  );
}

const GEO = ['pts', 'center', 'rot', 'len', 'wid', 'r', 'areaOverride'];
// selected excavation pit: angle, ±15°, done / cancel (handles come from pitLayers)
export function PitEditBar({ t, pit, patch, onClose, onDuplicate }) {
  const snap = useRef(null);
  if (!snap.current || snap.current.id !== pit.id) snap.current = { id: pit.id, ...Object.fromEntries(GEO.map((k) => [k, pit[k]])) };
  const rotBy = (d) => {
    const r = (pit.rot || 0) - d;
    if (pit.shape === 'rect') return patch(pit.id, { ...pit, rot: r, pts: rectFromParams(pit.center || pitCenter(pit), pit.len, pit.wid, r) });
    patch(pit.id, { ...pit, rot: r, pts: rotatePts(pit.pts, pitCenter(pit), -d) });
  };
  const flip = (axis) => patch(pit.id, { ...pit, pts: mirror(pit.pts, axis) });
  const dup = () => {
    const c = pit.center || pitCenter(pit), kx = mPerLon(c[0]);
    const lons = pit.shape === 'circle' ? [c[1] - pit.r / kx, c[1] + pit.r / kx] : pit.pts.map((p) => p[1]);
    const dLon = Math.max(...lons) - Math.min(...lons) + 2 / kx;
    onDuplicate({ ...pit, pts: pit.pts && moveBy(pit.pts, 0, dLon), center: pit.center && [pit.center[0], pit.center[1] + dLon] });
  };
  const cancel = () => { const { id, ...g } = snap.current; patch(pit.id, { ...pit, ...g }); onClose(); };
  return createPortal(
    <div className="ae-bar ae-top" data-testid="pit-edit-bar">
      <div className="ae-title"><Move size={15} /> <b>{pit.name}</b><small>{t.aePitHint}</small></div>
      {pit.shape !== 'circle' && (
        <div className="ae-row">
          <button className="btn btn-outline btn-sm" data-testid="pit-rot-minus" onClick={() => rotBy(-15)}><RotateCcw size={14} /> 15°</button>
          <span className="ae-deg" data-testid="pit-rot-display">{norm(-(pit.rot || 0))}°</span>
          <button className="btn btn-outline btn-sm" data-testid="pit-rot-plus" onClick={() => rotBy(15)}><RotateCw size={14} /> 15°</button>
        </div>
      )}
      <div className="ae-row">
        {pit.shape === 'polygon' && <button className="btn btn-outline btn-sm" data-testid="pit-mirror-lr" onClick={() => flip('lr')}><FlipHorizontal2 size={14} /> {t.aeMirrorLr}</button>}
        {pit.shape === 'polygon' && <button className="btn btn-outline btn-sm" data-testid="pit-mirror-ud" onClick={() => flip('ud')}><FlipVertical2 size={14} /> {t.aeMirrorUd}</button>}
        <button className="btn btn-outline btn-sm" data-testid="pit-duplicate" onClick={dup}><Copy size={14} /> {t.aeDup}</button>
      </div>
      <div className="ae-row">
        <button className="btn btn-outline btn-sm" data-testid="pit-edit-cancel" onClick={cancel}><X size={14} /> {t.btnCancel}</button>
        <button className="btn btn-green btn-sm" data-testid="pit-edit-done" onClick={onClose}><Check size={14} /> {t.btnFinish}</button>
      </div>
    </div>,
    document.body,
  );
}
